import {
  Button,
  TextInput,
  Text,
  Loader,
  Stack,
  Center,
  Paper,
  Box,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { displayErrorMessage } from "../shared/ErrorMessage";
import { initialize, updateField } from "../slices/form/emailLoginCompletion";
import { useEffect, useState } from "react";
import { useAuth } from "../authentication/AuthProvider";
import { useLocation } from "wouter";
import { Logo } from "../shared/components/Logo";

const EmailLoginCompletion = () => {
  const { signInUsingEmailLink } = useAuth();
  const { data } = useAppSelector(
    (state: RootState) => state.emailCompletionForm
  );
  const [_, navigate] = useLocation();

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      initialize({
        email: window.localStorage.getItem("emailForSignIn") ?? undefined,
      })
    );
  }, []);

  const [isTrying, setTrying] = useState(false);

  useEffect(() => {
    const email = window.localStorage.getItem("emailForSignIn") ?? undefined;
    if (email != null && email !== "") {
      // If email is already set, we can try to sign in immediately
      (async () => {
        try {
          setTrying(true);
          await trySignInUsingEmailLink(email);
        } catch (error) {
          console.error("Error signing in:", error);
        } finally {
          setTrying(false);
        }
      })();
    }
    dispatch(
      initialize({
        email,
      })
    );
  }, []);

  const trySignInUsingEmailLink = async (email: string) => {
    await signInUsingEmailLink(email);
    window.localStorage.removeItem("emailForSignIn");
    const urlParams = new URLSearchParams(window.location.search);
    const encodedRedirectUrl = urlParams.get("redirect");
    if (encodedRedirectUrl) {
      const decodedRedirectUrl = decodeURIComponent(encodedRedirectUrl);
      navigate(decodedRedirectUrl);
    } else {
      const nextPage = localStorage.getItem("intendedUrl");
      localStorage.removeItem("intendedUrl");
      navigate(nextPage ?? "/");
    }
  };

  const handleClickLogin = async () => {
    if (data === undefined) {
      console.error("Data is undefined but should not be");
      return;
    }
    if (data.email == null) {
      displayErrorMessage("Email is required.");
      return;
    }

    try {
      await trySignInUsingEmailLink(data.email);
    } catch (error) {
      displayErrorMessage(
        "Make sure that the email address matches the email used to register and that you clicked on the most recent login email link."
      );
      return;
    }
  };

  if (!data || isTrying) {
    return <Loader color="blue" />;
  }

  return (
    <Center style={{ height: "100vh" }}>
      <Stack align="center" justify="center" gap="sm">
        <Box w={100}>
          <Logo />
        </Box>
        <Text size="lg">Complete Email Login</Text>
        <Paper>Enter your email address for this event</Paper>
        <TextInput
          required
          mt="md"
          label="Email"
          type="email"
          autoComplete="email"
          id="email"
          name="email"
          style={{ width: "300px" }}
          value={data.email ?? ""}
          onChange={(v) => {
            dispatch(
              updateField({
                field: "email",
                value: v.currentTarget.value,
              })
            );
          }}
        />
        <Button
          mt="md"
          style={{ width: "250px" }}
          onClick={handleClickLogin}
          disabled={data.email == null}
        >
          Login
        </Button>
      </Stack>
    </Center>
  );
};

export default EmailLoginCompletion;
